import { FC, useEffect, useState, useCallback } from "react";
import { Activation, License } from "../../model";
import moment from "moment";

import "./LicenseKeysLicenseActivations.scss";
import { useAppDispatch, useAppSelector } from "../../../../assets/hooks/hooks";
import { Permissions } from "../../../../models/models";
import { hasPermission } from "../../../../assets/helpers/helpers";

import { ActivationLimitEditor } from "./ActivationLimitEditor";
import RemoveButton from "../../../../assets/components/RemoveButton/RemoveButton";
import ActivationLimitEditorActionButtons from "./ActivationLimitEditorActionButtons/ActivationLimitEditorActionButtons";

import {
    useRemoveActivationsLicenseMutation,
    useUpdateActivationLimitLicenseMutation,
} from "../../../../redux/RTKQuery/api";

import { toast } from "react-toastify";
import Loader from "../../../../assets/components/Loader/Loader";
import { Loading, uiSlicedActions } from "../../../../redux/ui-slice";

type Props = {
    activationList: Activation[];
    allowedMachineActivationCount: number;
    licenseId: License["id"];
    licenseKey: License["key"];
};

export const LicenseKeysLicenseActivations: FC<Props> = ({
    activationList,
    allowedMachineActivationCount,
    licenseId,
    licenseKey,
}) => {
    const dispatch = useAppDispatch();
    const userAuth = useAppSelector((state) => state.initializeApp);
    const loading: Loading = useAppSelector((state) => state.ui.loading);

    const [
        removeActivations,
        {
            isLoading: isRemovingActivations,
            isSuccess: isSuccessRemovingActivations,
            isError: isErrorRemovingActivations,
            reset: resetRemoveActivations,
        },
    ] = useRemoveActivationsLicenseMutation();

    const [
        updateActivationLimit,
        {
            isLoading: isUpdatingLimit,
            isSuccess: isSuccessUpdatingLimit,
            isError: isErrorUpdatingLimit,
            reset: resetUpdateLimit,
        },
    ] = useUpdateActivationLimitLicenseMutation();

    const [localActivations, setLocalActivations] =
        useState<Activation[]>(activationList);
    const [initialActivations] = useState<Activation[]>(activationList);

    const [newLimit, setNewLimit] = useState(allowedMachineActivationCount);
    const [initialLimit] = useState(allowedMachineActivationCount);

    const [dirty, setDirty] = useState(false);
    const [limitError, setLimitError] = useState<string | null>(null);

    useEffect(() => {
        if (localActivations.length > newLimit) {
            setLocalActivations((prev) => prev.slice(0, newLimit));
            setDirty(true);
        }
    }, [newLimit, localActivations.length]);

    useEffect(() => {
        if (isSuccessRemovingActivations) {
            toast.success("Activation(s) successfully removed");
            resetRemoveActivations();
        }

        if (isErrorRemovingActivations) {
            toast.error("Failed to remove activation(s)");
            resetRemoveActivations();
        }
    }, [
        isSuccessRemovingActivations,
        isErrorRemovingActivations,
        resetRemoveActivations,
    ]);

    useEffect(() => {
        if (isSuccessUpdatingLimit) {
            toast.success("Activation limit successfully updated");
            resetUpdateLimit();
        }

        if (isErrorUpdatingLimit) {
            toast.error("Failed to update activation limit");
            resetUpdateLimit();
        }
    }, [isSuccessUpdatingLimit, isErrorUpdatingLimit, resetUpdateLimit]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal = isRemovingActivations || isUpdatingLimit;

        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden")
        );
    }, [isRemovingActivations, isUpdatingLimit, dispatch]);

    useEffect(() => {
        handleLoadingState();
    }, [handleLoadingState]);

    const handleRemoveActivation = (machineId: string) => {
        setLocalActivations((prev) =>
            prev.filter((act) => act.machineId !== machineId)
        );
        setDirty(true);
    };

    const handleSave = async () => {
        if (limitError) return;

        try {
            if (localActivations.length < activationList.length) {
                const removedActivations = activationList
                    .filter(
                        (act) =>
                            !localActivations.find(
                                (localAct) =>
                                    localAct.machineId === act.machineId
                            )
                    )
                    .map((act) => act.id);

                await removeActivations({
                    licenseKey,
                    activationIds: removedActivations,
                }).unwrap();
            }

            if (newLimit !== allowedMachineActivationCount) {
                await updateActivationLimit({
                    licenseId,
                    allowedMachineActivationCount: newLimit,
                }).unwrap();
            }

            setDirty(false);
        } catch (e) {
            console.error(e);
        }
    };

    const handleRevert = () => {
        setLocalActivations(initialActivations);
        setNewLimit(initialLimit);
        setLimitError(null);
        setDirty(false);
    };

    return (
        <div className="license_activations">
            <Loader loading={loading} />

            {/* HEADER */}
            <div className="license_activations__title">
                <div className="license_activations__title_text">
                    Allowed activations:
                </div>
                <div className="license_activations__title_count">
                    {localActivations.length}/{newLimit}
                </div>
            </div>

            <div className="license_activations__form">
                {hasPermission(
                    userAuth.permissions,
                    Permissions.LICENSE_EDIT
                ) && (
                    <>
                        <ActivationLimitEditor
                            value={newLimit}
                            currentCount={localActivations.length}
                            maxLimit={20}
                            onChange={(value, err, dirtyLimit) => {
                                if (!err) setNewLimit(value);

                                setLimitError(err);

                                if (dirtyLimit) setDirty(true);
                            }}
                        />

                        <ActivationLimitEditorActionButtons
                            onSave={handleSave}
                            onReset={handleRevert}
                            disabled={
                                loading !== "hidden" ||
                                !dirty ||
                                Boolean(limitError)
                            }
                        />
                    </>
                )}
            </div>

            {/* ACTIVATION LIST */}
            <div className="list_activations">
                {localActivations.map((activation, idx) => (
                    <div key={activation.machineId} className="item">
                        <div className="item__row item__row_header">
                            <div className="item__text">{idx + 1}</div>

                            {hasPermission(
                                userAuth.permissions,
                                Permissions.LICENSE_DELETE
                            ) && (
                                <RemoveButton
                                    position="right"
                                    img="top"
                                    onClick={() =>
                                        handleRemoveActivation(
                                            activation.machineId
                                        )
                                    }
                                />
                            )}
                        </div>

                        <div className="item__row">
                            <div className="item__text">
                                Last Activation Date:
                            </div>
                            <div className="item__text item__text_time">
                                {moment
                                    .unix(activation.activationDate)
                                    .format("LLL")}
                            </div>
                        </div>

                        <div className="item__row">
                            <div className="item__text">Machine ID:</div>
                            <div className="item__text item__text_white_bold">
                                {activation.machineId}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
